/**
 * EmailCard - v2 content for Gmail send actions.
 *
 * Renders a compact "To:" recipient line and the full EmailActionPreview
 * (handles auto-save, attachments, etc.). Shell is provided by the parent.
 */

import { useCallback } from 'react';

import type {
  GmailSendPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isGmailSendPayload } from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import EmailActionPreview from './artifacts/EmailActionPreview';
import { ActionError } from './shared';

// ─── Recipient display ────────────────────────────────────────────────────────

function formatRecipients(to: string[]): string {
  if (to.length === 0) {
    return '—';
  }
  if (to.length === 1) {
    return to[0];
  }
  if (to.length <= 3) {
    return to.join(', ');
  }
  return `${to.slice(0, 2).join(', ')} +${to.length - 2} more`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function EmailCard({
  action,
  mode,
  onUpdate,
  proposalId,
  orgId,
  isProcessing,
  onOpenThread,
  versionCount,
  onShowVersionHistory,
}: ActionRendererProps) {
  const handleActionUpdate = useCallback(
    (updated: ActionRendererProps['action']) => {
      onUpdate?.(updated);
    },
    [onUpdate]
  );

  if (!isGmailSendPayload(action.payload)) {
    return <ActionError message='Invalid email action payload' mode={mode} />;
  }

  const typedAction = action as ProposedAction<GmailSendPayload>;
  const params = typedAction.payload.action_params;

  // NOTE: The "Track opens" toggle lives in EmailActionPreview, not here.
  // EmailCard is bypassed for gmail_send actions in the proposal detail view —
  // ActionVersionTimeline renders VersionItem → EmailActionPreview directly.
  // Putting the toggle here would make it invisible. See plan #3536 checkout fix.
  return (
    <>
      {/* Compact "To:" recipient line */}
      <div className='px-4 py-2 border-b border-border-secondary bg-bg-secondary/50'>
        <p className='text-xs text-fg-tertiary'>
          <span className='font-medium text-fg-secondary'>To:</span>{' '}
          <span>{formatRecipients(params.to ?? [])}</span>
        </p>
      </div>

      {/* Full email editor with auto-save, attachments, threading */}
      {proposalId ? (
        <EmailActionPreview
          action={typedAction}
          proposalId={proposalId}
          editable={mode === 'full' && !isProcessing}
          onActionUpdate={handleActionUpdate}
          isProcessing={isProcessing}
          orgId={orgId}
          onOpenThread={onOpenThread}
          versionCount={versionCount}
          onShowVersionHistory={onShowVersionHistory}
        />
      ) : (
        <div className='px-4 py-3 text-sm text-fg-secondary'>
          {params.subject || 'Email preview unavailable'}
        </div>
      )}
    </>
  );
}
